import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  currentStep: 1,
  totalSteps: 5,
  
  // Step 1: About You
  fullName: '',
  email: '',
  gender: '', // 'male' | 'female' | 'other'
  licenseNumber: '', // optional
  
  // Step 2: Clinic Info
  clinicName: '',
  clinicType: '', // see clinicTypesConfig.js for all valid values
  customTypeName: '', // free-text name when clinicType === 'other'
  logoFile: null,
  logoPreview: null,
  businessId: '', // ח"פ / עוסק מורשה
  clinicAddress: '',
  vatType: '', // 'exempt' | 'licensed' | 'company'
  
  // Step 3: Specializations
  specializations: [],
  ageRange: [], // 'infants' | 'toddlers' | 'preschool' | 'children' | 'teens' | 'adults' | 'elderly'
  subSpecializations: [], // specific sub-specializations
  
  // Step 4: Work preferences
  // General (default) treatment type → clinic.defaultHourlyRate / defaultMeetingTime
  // + first treatmentType category.
  generalTreatmentName: 'טיפול כללי',
  defaultHourlyRate: '',
  defaultMeetingTime: 45, // 15, 30, 45, 60 or custom
  // Additional treatment types. null = step never visited → seed suggestions.
  // Row: { key, name, duration: ''|number, rate: ''|number, color, isSuggested }
  treatmentTypes: null,
  treatmentTypesSeededFor: '', // clinicType the suggested rows were generated for
  // Existing clinic feature flags (opt-in, default off — same as settings)
  clinicManagesCouplesAsPatients: false,
  tempCustomersEnabled: false,
  onlineMeetingsEnabled: false,
  // First hour shown in the Day/Week calendar grid → clinic.calendarStartHour (1–20, default 5)
  calendarStartHour: 5,
  
  // Step 5: Phone Verification & Onboarding
  phone: '',
  verificationId: null,
  isCodeSent: false,
  verificationCode: '',
  termsAccepted: false,
  onboardingCallDate: null, // optional
  
  // Status
  isSubmitting: false,
  error: null
};

const registrationSlice = createSlice({
  name: 'registration',
  initialState,
  reducers: {
    // Navigation
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps) {
        state.currentStep += 1;
      }
    },
    prevStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    goToStep: (state, action) => {
      const step = action.payload;
      if (step >= 1 && step <= state.totalSteps) {
        state.currentStep = step;
      }
    },
    
    // Step 1: About You
    setAboutYou: (state, action) => {
      const { fullName, email, gender, licenseNumber } = action.payload;
      state.fullName = fullName;
      state.email = email;
      state.gender = gender;
      state.licenseNumber = licenseNumber || '';
    },
    
    // Step 2: Clinic Info
    setClinicInfo: (state, action) => {
      const {
        clinicName,
        clinicType,
        customTypeName,
        businessId,
        clinicAddress,
        vatType
      } = action.payload;
      state.clinicName = clinicName;
      state.clinicType = clinicType;
      if (customTypeName !== undefined) state.customTypeName = customTypeName;
      if (businessId !== undefined) state.businessId = businessId;
      if (clinicAddress !== undefined) state.clinicAddress = clinicAddress;
      if (vatType !== undefined) state.vatType = vatType;
    },
    setLogo: (state, action) => {
      state.logoFile = action.payload.file;
      state.logoPreview = action.payload.preview;
    },
    clearLogo: (state) => {
      state.logoFile = null;
      state.logoPreview = null;
    },
    
    // Step 3: Specializations
    setSpecializations: (state, action) => {
      const { specializations, ageRange, subSpecializations } = action.payload;
      state.specializations = specializations || [];
      state.ageRange = ageRange || [];
      state.subSpecializations = subSpecializations || [];
    },
    
    // Step 4: Work preferences
    setPreferences: (state, action) => {
      const {
        generalTreatmentName,
        defaultHourlyRate,
        defaultMeetingTime,
        treatmentTypes,
        treatmentTypesSeededFor,
        clinicManagesCouplesAsPatients,
        tempCustomersEnabled,
        onlineMeetingsEnabled,
        calendarStartHour
      } = action.payload;
      if (generalTreatmentName !== undefined) state.generalTreatmentName = generalTreatmentName;
      if (defaultHourlyRate !== undefined) state.defaultHourlyRate = defaultHourlyRate;
      if (defaultMeetingTime !== undefined) state.defaultMeetingTime = defaultMeetingTime;
      if (treatmentTypes !== undefined) state.treatmentTypes = treatmentTypes;
      if (treatmentTypesSeededFor !== undefined) state.treatmentTypesSeededFor = treatmentTypesSeededFor;
      if (clinicManagesCouplesAsPatients !== undefined) state.clinicManagesCouplesAsPatients = clinicManagesCouplesAsPatients === true;
      if (tempCustomersEnabled !== undefined) state.tempCustomersEnabled = tempCustomersEnabled === true;
      if (onlineMeetingsEnabled !== undefined) state.onlineMeetingsEnabled = onlineMeetingsEnabled === true;
      if (calendarStartHour !== undefined) state.calendarStartHour = calendarStartHour;
    },
    
    // Step 5: Phone Verification
    setPhone: (state, action) => {
      state.phone = action.payload;
    },
    setCodeSent: (state, action) => {
      state.isCodeSent = action.payload;
    },
    setVerificationCode: (state, action) => {
      state.verificationCode = action.payload;
    },
    setVerificationId: (state, action) => {
      state.verificationId = action.payload;
    },
    setTermsAccepted: (state, action) => {
      state.termsAccepted = action.payload;
    },
    setOnboardingCallDate: (state, action) => {
      state.onboardingCallDate = action.payload;
    },
    
    // Status
    setSubmitting: (state, action) => {
      state.isSubmitting = action.payload;
    },
    setRegistrationError: (state, action) => {
      state.error = action.payload;
      state.isSubmitting = false;
    },
    clearRegistrationError: (state) => {
      state.error = null;
    },
    
    // Reset
    resetRegistration: () => initialState
  }
});

export const {
  nextStep,
  prevStep,
  goToStep,
  setAboutYou,
  setClinicInfo,
  setLogo,
  clearLogo,
  setSpecializations,
  setPreferences,
  setPhone,
  setCodeSent,
  setVerificationCode,
  setVerificationId,
  setTermsAccepted,
  setOnboardingCallDate,
  setSubmitting,
  setRegistrationError,
  clearRegistrationError,
  resetRegistration
} = registrationSlice.actions;

// Selectors
export const selectCurrentStep = (state) => state.registration.currentStep;
export const selectTotalSteps = (state) => state.registration.totalSteps;

export const selectAboutYou = (state) => ({
  fullName: state.registration.fullName,
  email: state.registration.email,
  gender: state.registration.gender,
  licenseNumber: state.registration.licenseNumber
});

export const selectClinicInfo = (state) => ({
  clinicName: state.registration.clinicName,
  clinicType: state.registration.clinicType,
  customTypeName: state.registration.customTypeName,
  businessId: state.registration.businessId,
  clinicAddress: state.registration.clinicAddress,
  vatType: state.registration.vatType
});

export const selectPreferences = (state) => ({
  generalTreatmentName: state.registration.generalTreatmentName,
  defaultHourlyRate: state.registration.defaultHourlyRate,
  defaultMeetingTime: state.registration.defaultMeetingTime,
  treatmentTypes: state.registration.treatmentTypes,
  treatmentTypesSeededFor: state.registration.treatmentTypesSeededFor,
  clinicManagesCouplesAsPatients: state.registration.clinicManagesCouplesAsPatients,
  tempCustomersEnabled: state.registration.tempCustomersEnabled,
  onlineMeetingsEnabled: state.registration.onlineMeetingsEnabled,
  calendarStartHour: state.registration.calendarStartHour
});

export const selectLogo = (state) => ({
  file: state.registration.logoFile,
  preview: state.registration.logoPreview
});

export const selectSpecializations = (state) => ({
  specializations: state.registration.specializations,
  ageRange: state.registration.ageRange,
  subSpecializations: state.registration.subSpecializations
});

// Alias for backward compatibility
export const selectSpecializationsData = (state) => ({
  specializations: state.registration.specializations,
  ageRange: state.registration.ageRange,
  subSpecializations: state.registration.subSpecializations
});

export const selectPhoneInfo = (state) => ({
  phone: state.registration.phone,
  isCodeSent: state.registration.isCodeSent,
  verificationCode: state.registration.verificationCode,
  termsAccepted: state.registration.termsAccepted,
  onboardingCallDate: state.registration.onboardingCallDate
});

export const selectRegistrationData = (state) => ({
  // About You
  fullName: state.registration.fullName,
  email: state.registration.email,
  gender: state.registration.gender,
  licenseNumber: state.registration.licenseNumber,
  // Clinic Info
  clinicName: state.registration.clinicName,
  clinicType: state.registration.clinicType,
  customTypeName: state.registration.customTypeName,
  defaultHourlyRate: state.registration.defaultHourlyRate,
  defaultMeetingTime: state.registration.defaultMeetingTime,
  businessId: state.registration.businessId,
  clinicAddress: state.registration.clinicAddress,
  vatType: state.registration.vatType,
  // Specializations
  specializations: state.registration.specializations,
  ageRange: state.registration.ageRange,
  subSpecializations: state.registration.subSpecializations,
  // Preferences
  generalTreatmentName: state.registration.generalTreatmentName,
  treatmentTypes: state.registration.treatmentTypes,
  clinicManagesCouplesAsPatients: state.registration.clinicManagesCouplesAsPatients,
  tempCustomersEnabled: state.registration.tempCustomersEnabled,
  onlineMeetingsEnabled: state.registration.onlineMeetingsEnabled,
  calendarStartHour: state.registration.calendarStartHour,
  // Phone
  phone: state.registration.phone,
  termsAccepted: state.registration.termsAccepted,
  onboardingCallDate: state.registration.onboardingCallDate,
  logoFile: state.registration.logoFile
});

export const selectIsSubmitting = (state) => state.registration.isSubmitting;
export const selectRegistrationError = (state) => state.registration.error;

// Legacy selectors for backward compatibility
export const selectPersonalInfo = (state) => ({
  firstName: state.registration.fullName?.split(' ')[0] || '',
  lastName: state.registration.fullName?.split(' ').slice(1).join(' ') || '',
  email: state.registration.email
});

export default registrationSlice.reducer;
